import { Injectable, QueryList } from "@angular/core";
import { NgModel } from "@angular/forms";
import { Entity, JobMaterial, NicknameValidationData } from "@common/types";
import { defaultString } from "@common/util/default-string";
import { DataManagerService } from "@services/data-manager.service";
import { TranslationService } from "@services/translation.service";
import { EntityQuery, Predicate } from "breeze-client";
import { GenerateAnimalJobMaterialNicknamesRequest, GetAnimalJobMaterialNicknamesRequest, NicknameData, NicknameDataAccessService } from "@services/nickname-data-access.service";
import { AnimalJobMaterialExtended } from "../../models/extended-job-material";
import { JobPharmaCoreService } from "../../services";
type NicknameDataExtended = NicknameData & NicknameValidationData;


@Injectable()
export class JobPharmaAnimalNicknameService {
    constructor(
        private dataManager: DataManagerService,
        private jobPharmaCoreService: JobPharmaCoreService,
        private translationService: TranslationService,
        private nicknameDataAccessService: NicknameDataAccessService
    ) {
        const jobsTerm = this.translationService.translate('Jobs').toLowerCase();

        this.nicknamesOnJobsTerm = `Nicknames on ${jobsTerm}`;
        this.uniqueValidationMessage = `${this.nicknamesOnJobsTerm} must be unique`;
    }

    private nicknamesOnJobsTerm = 'Nicknames on jobs';
    private uniqueValidationMessage = 'Nicknames on jobs must be unique';

    // Current job key
    private jobKey: number;
    // Job materials loaded in the individual animal table
    private loadedJobMaterials: AnimalJobMaterialExtended[];
    // Maps C_JobMaterial to NicknameDataExtended.
    // Because service is provided at the facet level, each facet has its own map.
    // On job change, the map is recreated.
    private nicknameData: Map<number, NicknameDataExtended> = new Map();

    /**
     * Resets the state on job change
     */
    async onJobChanged(jobKey: number): Promise<void> {
        this.jobKey = jobKey;
        this.loadedJobMaterials = [];
        this.nicknameData = new Map();

        if (this.jobKey > 0) {
            // Load all nicknames from the server and validate them
            await this.validateAllJobMaterialNicknames();
        }
    }

    /**
     * Loads job materials
     */
    onLoadJobMaterials(jobKey: number, loadedJobMaterials: AnimalJobMaterialExtended[]) {
        if (this.jobKey != jobKey) {
            // Create a new map if the job is changed
            this.nicknameData = new Map();
        }

        // Remember job key and loaded joba materials
        this.jobKey = jobKey;
        this.loadedJobMaterials = loadedJobMaterials;

        // Validate nickname and update loaded job materials with nickname data
        this._updateLoadedJobMaterialsWithNickname();
    }

    /**
     * Generates nicknames on study and update loaded job materials with new nicknames
     * @returns Toastr message that describes the changes
     */
    async generateNicknameOnJob(selectedJobMaterialKeys: number[], bulkNicknamePrefix: string, bulkNicknameSuffix: string, bulkNicknameCounter: number): Promise<string> {
        if (!this._hasLoadedJobMaterials() && (!selectedJobMaterialKeys || !selectedJobMaterialKeys.length)) {
            return null;
        }
        const removedJobMaterialKeys = Array.from(this.jobPharmaCoreService.getRemovedAnimalJobMaterials(this.jobKey).keys());
        const prefix = defaultString(bulkNicknamePrefix, '');
        const suffix = defaultString(bulkNicknameSuffix, '');
        const data: GenerateAnimalJobMaterialNicknamesRequest = {
            JobKey: this.jobKey,
            Prefix: prefix,
            Suffix: suffix,
            Counter: bulkNicknameCounter,
            JobMaterialKeys: selectedJobMaterialKeys?.length ? selectedJobMaterialKeys : null,
            RemovedJobMaterialKeys: removedJobMaterialKeys
        };

        // Generate nickname on studies
        const updatedNicknames = await this.nicknameDataAccessService.generateAnimalJobMaterialNicknames(data);

        // Update nickname data with new nicknames
        Object.entries(updatedNicknames).forEach(([jobMaterialKeyStr, nicknames]) => {
            this.nicknameData.set(Number(jobMaterialKeyStr), { ...nicknames, IsNicknameValid: true });
        });

        // Validate nickname and update loaded job materials with nickname data
        this._updateLoadedJobMaterialsWithNickname();

        const messageDetails = selectedJobMaterialKeys?.length
            ? (selectedJobMaterialKeys?.length + ' selected animal' + (selectedJobMaterialKeys?.length > 1 ? `s ` : ''))
            : 'all animals';

        return `${this.nicknamesOnJobsTerm} updated for ${messageDetails}.`
    }

    /**
     * Updates unloaded job materials with nickname data.
     */
    async onSaveChanges(): Promise<void> {
        if (!this.isNicknameChanged()) {
            return;
        }

        // Retrieve job materials that have not been loaded, but can be changed using Nickname Bulk Update
        const unloadedJobMaterials = await this._getUnloadedJobMaterials();
        if (!unloadedJobMaterials?.length) {
            return;
        }

        // Update loaded job materials array
        unloadedJobMaterials.forEach(jobMaterial => this.loadedJobMaterials.push(jobMaterial));

        // Updated job materials with nickname data to apply changes
        this._updateLoadedJobMaterialsWithNicknameData();
    }

    /**
     * Removes job material 
     */
    onJobMaterialRemove(jobMaterial: AnimalJobMaterialExtended) {
        if (this.loadedJobMaterials.includes(jobMaterial)) {
            this.loadedJobMaterials = this.loadedJobMaterials.filter(x => x !== jobMaterial);
        }

        // Validate nickname and update loaded job materials with nickname data
        this._updateLoadedJobMaterialsWithNickname();
    }

    /**
     * Updates nickname data with job material when job material is changed
     */
    onNicknameChange(updatedJobMaterial: AnimalJobMaterialExtended) {
        // Update nickname with job material changes
        this.nicknameData.set(updatedJobMaterial.C_JobMaterial_key, {
            Nickname: updatedJobMaterial.Nickname,
            NicknameSortable: updatedJobMaterial.NicknameSortable,
            IsNicknameValid: true
        });

        // Validate nickname and update loaded job materials with nickname data
        this._updateLoadedJobMaterialsWithNickname();
    }

    /**
     * Returns value indicating whether exists job material with updated Nickname property
     * @returns
     */
    isNicknameChanged(): boolean {
        return this._hasLoadedJobMaterials() &&
            this.loadedJobMaterials.some(x => 'Nickname' in (x as JobMaterial as Entity<JobMaterial>).entityAspect.originalValues);
    }

    /**
     * Validates nickname controls and sets errors if needed
     */
    validateNicknameControls(nicknameControls: QueryList<NgModel>) {
        const currentNicknames = Array.from(this.nicknameData.values()).map(x => x.Nickname?.trim());

        nicknameControls.forEach(control => {
            const controlNickname = String(control.model)?.trim();

            if (controlNickname && currentNicknames.filter(nickname => nickname === controlNickname).length > 1) {
                control.control.setErrors({ 'unique': this.uniqueValidationMessage });
            } else {
                control.control.setErrors(null);
            }
        });
    }

    /**
     * Validates job materials' nickname data and returns the validation message if needed.
     */
    async validateAllJobMaterialNicknames(): Promise<string> {
        const request: GetAnimalJobMaterialNicknamesRequest = {
            JobKey: this.jobKey,
            CurrentNicknames: Object.fromEntries(
                [...this.nicknameData.entries()].map(([key, value]) => [key, value.Nickname])
            )
        };
        // Regenerate sortable nicknames and append missing data from server.
        // This is necessary because sortable nicknames are only updated on the server, 
        // and also because loaded nicknames that have been changed may conflict with unloaded nicknames.
        const allNicknames = await this.nicknameDataAccessService.getAnimalJobMaterialNicknames(request);
        // Update nickname data with the data from server to check all nicknames.
        // Removed job materials will be removed from nickname data.
        this._updateNicknameData(allNicknames);
        // Validate nickname and update loaded job materials with nickname data
        this._updateLoadedJobMaterialsWithNickname();

        const isNicknamesValid = ![...this.nicknameData.values()].some(x => !x.IsNicknameValid);

        return isNicknamesValid ? null : this.uniqueValidationMessage;
    }

    /**
     * Returns value indicating whether loaded job materials exist
     * @returns
     */
    private _hasLoadedJobMaterials(): boolean {
        return this.jobKey > 0 && this.loadedJobMaterials && this.loadedJobMaterials.length > 0;
    }

    /**
     * Updates job materials with nickname data when jobMaterials are loaded, modified, or nickname data changed.
     * Validates job material nicknames.
     * Updates job materials with nickname data to show in UI.
     */
    private _updateLoadedJobMaterialsWithNickname() {
        // Add missing jobMaterials to map
        this._addLoadedJobMaterialsToNicknameData();
        // Delete from map removed job materials
        this._deleteRemovedJobMaterialsFromNicknameData();
        // Validate nicknames
        this._validateNicknameData();
        // Update job materials with nickname data
        this._updateLoadedJobMaterialsWithNicknameData();
    }

    /**
     *  Updates nickname with missing job materials 
     */
    private _addLoadedJobMaterialsToNicknameData() {
        if (!this._hasLoadedJobMaterials()) {
            return;
        }

        // If we already have the data for the job material, we should not update the map so as not to lose our changes
        this.loadedJobMaterials
            .filter(jobMaterial => !this.nicknameData.has(jobMaterial.C_JobMaterial_key))
            .forEach(jobMaterial => this.nicknameData.set(
                jobMaterial.C_JobMaterial_key,
                this._jobMaterialToNicknameDataExtended(jobMaterial)
            ));
    }

    /**
     *  Updates nickname data with nicknames of job materials (excluding removed job materials)
     */
    private _updateNicknameData(nicknames: Record<number, NicknameData>) {
        Object.entries(nicknames).forEach(([jobMaterialKeyStr, nicknameData]) => {
            const jobMaterialKey = Number(jobMaterialKeyStr);

            if (!this.nicknameData.has(jobMaterialKey)) {
                // Add data from server if not exists
                this.nicknameData.set(
                    jobMaterialKey,
                    this._nicknameDataToNicknameDataExtended(nicknameData)
                );
            } else {
                // Update existing data with data from server
                const nicknames = this.nicknameData.get(jobMaterialKey);
                nicknames.Nickname = nicknameData.Nickname;
                nicknames.NicknameSortable = nicknameData.NicknameSortable;
            }
        });
    }

    /**
     * Deletes removed job materials from nickname data
     */
    private _deleteRemovedJobMaterialsFromNicknameData() {
        const removedJobMaterials = this.jobPharmaCoreService.getRemovedAnimalJobMaterials(this.jobKey);

        Array.from(removedJobMaterials.keys()).forEach(jobMaterialKey => {
            if (this.nicknameData.has(jobMaterialKey)) {
                this.nicknameData.delete(jobMaterialKey);
            }
        });
    }

    /**
     *  Updates job materials with existing nickname data 
     */
    private _updateLoadedJobMaterialsWithNicknameData(): void {
        if (!this._hasLoadedJobMaterials()) {
            return;
        }
        this.loadedJobMaterials.forEach(jobMaterial => {
            const nicknameData = this.nicknameData.get(jobMaterial.C_JobMaterial_key);
            jobMaterial.Nickname = nicknameData.Nickname;
            jobMaterial.NicknameSortable = nicknameData.NicknameSortable;
            jobMaterial.IsNicknameValid = nicknameData.IsNicknameValid;
        });
    }

    /**
     *  Validates nickname data and sets IsNicknameValid for records with non-unique nicknames 
     */
    private _validateNicknameData() {
        const nicknameCount = new Map<string, number>();

        for (const [, data] of this.nicknameData) {
            const nickname = data.Nickname?.trim();
            if (nickname) {
                nicknameCount.set(nickname, (nicknameCount.get(nickname) || 0) + 1);
            }
        }

        for (const [key, data] of this.nicknameData) {
            // Nickname is valid if it is empty or unique across all animals
            const item = this.nicknameData.get(key);
            if (!data.Nickname?.trim()) {
                item.IsNicknameValid = true
            } else {
                item.IsNicknameValid = nicknameCount.get(data.Nickname?.trim())! === 1;
            }
        }
    }

    /**
     * Returns unloaded animal job materials of the current job
     */
    private async _getUnloadedJobMaterials(): Promise<AnimalJobMaterialExtended[]> {
        const predicates: Predicate[] = [];
        const loadedJobMaterialKeys: number[] = this.loadedJobMaterials.map(x => x.C_JobMaterial_key);
        predicates.push(Predicate.create('C_Job_key', '==', this.jobKey));
        predicates.push(Predicate.create('C_JobMaterial_key', 'in', loadedJobMaterialKeys).not());
        predicates.push(Predicate.create('Material.cv_MaterialType.MaterialType', '==', 'Animal'));

        const query = EntityQuery
            .from("JobMaterials")
            .where(Predicate.and(predicates));

        return (await this.dataManager.executeQuery(query)).results as Entity<AnimalJobMaterialExtended>[] ?? [];
    }

    private _jobMaterialToNicknameDataExtended(jobMaterial: JobMaterial): NicknameDataExtended {
        return {
            Nickname: jobMaterial.Nickname,
            NicknameSortable: jobMaterial.NicknameSortable,
            IsNicknameValid: true
        };
    }

    private _nicknameDataToNicknameDataExtended(nicknameData: NicknameData): NicknameDataExtended {
        return {
            ...nicknameData,
            IsNicknameValid: true
        };
    }
}
